import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { getIntegrationsNavLinks } from '@src/utilities/topNavHelper';
import { omit } from 'lodash';
import { Confirm, DataTable, Form, SAVE_SERVER, SERVERS, TEST_SERVER, TrackableButton, emptyServer, useAlert, useLocale, useServers, } from 'pe-core-frontend';
import { useState } from 'react';
export const Servers = ({ clientId }) => {
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { items, loading, error, deleteServer } = useServers({ clientId });
    const [saveServer] = useMutation(SAVE_SERVER, {
        refetchQueries: [SERVERS],
    });
    const [selectedServer, setSelectedServer] = useState(null);
    const [serverToDelete, setServerToDelete] = useState(null);
    const FIELDS = [
        {
            name: 'name',
            label: t('Name'),
            type: 'text',
            required: true,
        },
        {
            name: 'hostname',
            label: t('Hostname'),
            type: 'text',
            required: true,
        },
        {
            name: 'port',
            label: t('Port'),
            type: 'text',
            required: true,
        },
        {
            name: 'username',
            label: t('Username'),
            type: 'text',
            required: true,
        },
        {
            name: 'password',
            label: t('Password'),
            type: 'password',
        },
        {
            name: 'sshKey',
            label: t('SSH Key'),
            type: 'textarea',
            plainText: true,
        },
    ];
    const handleValidate = (server) => {
        const errors = {};
        if (!server.name) {
            errors.name = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!server.hostname) {
            errors.hostname = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!server.port) {
            errors.port = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!server.username) {
            errors.username = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!server.password && !server.sshKey) {
            errors.sshKey = errors.password = t('Either {field} or {field2} must be provided', {
                values: { field: t('Password'), field2: t('SSH Key') },
            });
        }
        return errors;
    };
    const handleSave = async (server) => {
        try {
            await saveServer({
                variables: {
                    input: {
                        ...omit(server, ['__typename', 'createdAt', 'updatedAt']),
                        port: Number(server.port),
                        clientId,
                    },
                },
            });
            showAlert(t('Server saved'));
            setSelectedServer(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            showAlert(e.message, 'error');
        }
    };
    const [testServerQuery] = useLazyQuery(TEST_SERVER);
    const handleTest = async (server) => {
        try {
            const result = await testServerQuery({
                variables: {
                    input: {
                        ...omit(server, ['__typename', 'createdAt', 'updatedAt']),
                        port: Number(server.port),
                        clientId,
                    },
                },
            });
            if (result.data.testServer === true) {
                showAlert(t('Server connected successfully'));
            }
            else {
                showAlert(t('Server failed to connect'));
            }
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            showAlert(e.message, 'error');
        }
    };
    const handleDelete = async () => {
        if (serverToDelete) {
            try {
                await deleteServer({
                    variables: {
                        id: serverToDelete.id,
                    },
                });
                showAlert(t('Server deleted'));
                setServerToDelete(null);
            }
            catch (e) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                showAlert(e.message, 'error');
            }
        }
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Integrations'), navLinks: getIntegrationsNavLinks(t, clientId, 'servers'), actions: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Server', tag: 'header', variant: 'contained', className: 'button button--primary margin--bottom_16', onClick: () => setSelectedServer({ ...emptyServer, clientId }), children: t('Add Server') }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { loading: loading, disableColumnMenu: true, disableRowSelectionOnClick: true, rows: items || [], hideFooter: true, columns: [
                                    {
                                        field: 'name',
                                        headerName: t('Name'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    {
                                        field: 'hostname',
                                        headerName: t('Hostname'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    {
                                        field: 'port',
                                        headerName: t('Port'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    {
                                        field: 'username',
                                        headerName: t('Username'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    {
                                        field: 'action',
                                        headerName: t('Actions'),
                                        renderCell: ({ row }) => (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `server_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedServer(row), className: 'margin--right_8' }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `server_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => setServerToDelete(row) })] })),
                                        width: 100,
                                        sortable: false,
                                    },
                                ] }), selectedServer && (_jsx(Form, { title: selectedServer.id ? 'Edit Server' : 'Add Server', modal: true, obj: selectedServer, fields: FIELDS, onValidate: handleValidate, onSave: handleSave, onCancel: () => setSelectedServer(null), testable: true, onTest: handleTest })), serverToDelete && (_jsx(Confirm, { message: t("Are you sure you want to delete '{name}'?", {
                                    values: { name: serverToDelete.name },
                                }), onConfirm: handleDelete, onCancel: () => setServerToDelete(null) }))] }) }) })] }));
};
